import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Colors } from "../utils/colors";
import { API } from "../constant/apiConstant";
import { setAdmin } from "../features/auth/authSlice";
import { consumeAdminSessionMessage } from "../utils/adminSessionTimeout";

export default function PresenterLogin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [presenterId, setPresenterId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    severity: "info",
    message: ""
  });

  const showAlert = (severity, message) => {
    setAlert({
      open: true,
      severity,
      message
    });
  };

  useEffect(() => {
    const message = consumeAdminSessionMessage();
    if (message) {
      showAlert("warning", message);
    }
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!presenterId.trim() || !password || loading) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API.BASE_URL}/api/admin/auth/presenter/login`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          presenter_id: presenterId,
          password
        })
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.status) {
        showAlert("error", data?.message || "Presenter login failed");
        setLoading(false);
        return;
      }

      dispatch(setAdmin("presenter"));
      showAlert("success", "Presenter authenticated successfully");

      navigate(`/admin/draws/live/${data.assigned_draw_id}`, {
        replace: true,
        state: {
          draw: data.draw || null
        }
      });
    } catch (error) {
      console.log(error);
      showAlert("error", "Unable to connect to the server");
    }

    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "radial-gradient(circle at top, rgba(255,193,7,0.12), rgba(10,61,98,0.96) 34%, rgba(2,18,33,1) 100%)",
        px: 2,
        py: 4
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 460,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          boxShadow: "0 24px 60px rgba(0,0,0,0.25)"
        }}
      >
        <Stack spacing={2.5} component="form" onSubmit={handleSubmit}>
          <Stack spacing={1}>
            <Typography variant="h4" fontWeight={800} sx={{ color: Colors.secondary2 }}>
              Presenter Login
            </Typography>
         
          </Stack>

          {alert.open ? <Alert severity={alert.severity}>{alert.message}</Alert> : null}

          <TextField
            label="Presenter ID"
            value={presenterId}
            onChange={(event) => setPresenterId(event.target.value.toUpperCase())}
            fullWidth
          />

          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            fullWidth
          />

          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              bgcolor: Colors.accent,
              color: "#002b45",
              fontWeight: 800,
              py: 1.3
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: "#002b45" }} /> : "Run Assigned Draw"}
          </Button>

          <Button variant="text" onClick={() => navigate("/")}>
            Back to Admin Login
          </Button>
        </Stack>
      </Card>
    </Box>
  );
}
